/**
 * Secure Storage for Capacitor
 * Uses Capacitor Preferences for persistent key-value storage
 */

import { Preferences } from '@capacitor/preferences';
import { isNativePlatform } from './platform';

const STORAGE_PREFIX = 'insuracentral_';

/**
 * Set a value in storage
 */
export async function setItem(key: string, value: string): Promise<void> {
  const prefixedKey = `${STORAGE_PREFIX}${key}`;

  if (isNativePlatform()) {
    await Preferences.set({ key: prefixedKey, value });
  } else {
    localStorage.setItem(prefixedKey, value);
  }
}

/**
 * Get a value from storage
 */
export async function getItem(key: string): Promise<string | null> {
  const prefixedKey = `${STORAGE_PREFIX}${key}`;

  if (isNativePlatform()) {
    const { value } = await Preferences.get({ key: prefixedKey });
    return value;
  } else {
    return localStorage.getItem(prefixedKey);
  }
}

/**
 * Remove a value from storage
 */
export async function removeItem(key: string): Promise<void> {
  const prefixedKey = `${STORAGE_PREFIX}${key}`;

  if (isNativePlatform()) {
    await Preferences.remove({ key: prefixedKey });
  } else {
    localStorage.removeItem(prefixedKey);
  }
}

/**
 * Clear all storage (with prefix)
 */
export async function clear(): Promise<void> {
  if (isNativePlatform()) {
    const { keys } = await Preferences.keys();
    const ourKeys = keys.filter((k) => k.startsWith(STORAGE_PREFIX));
    for (const key of ourKeys) {
      await Preferences.remove({ key });
    }
  } else {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
}

/**
 * Get all keys
 */
export async function keys(): Promise<string[]> {
  if (isNativePlatform()) {
    const { keys: allKeys } = await Preferences.keys();
    return allKeys
      .filter((k) => k.startsWith(STORAGE_PREFIX))
      .map((k) => k.replace(STORAGE_PREFIX, ''));
  } else {
    const result: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        result.push(key.replace(STORAGE_PREFIX, ''));
      }
    }
    return result;
  }
}

/**
 * Set a JSON object in storage
 */
export async function setJSON<T>(key: string, value: T): Promise<void> {
  await setItem(key, JSON.stringify(value));
}

/**
 * Get a JSON object from storage
 */
export async function getJSON<T>(key: string): Promise<T | null> {
  const value = await getItem(key);
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

// Storage keys constants
export const STORAGE_KEYS = {
  // Auth
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_ID: 'user_id',
  USER_DATA: 'user_data',

  // Device
  DEVICE_ID: 'device_id',
  PUSH_TOKEN: 'push_token',
  VOIP_TOKEN: 'voip_token',
  PUSH_ENABLED: 'push_enabled',

  // App State
  LAST_SYNC: 'last_sync',
  PENDING_ACTIONS: 'pending_actions',
  CACHED_LEADS: 'cached_leads',
  CACHED_APPOINTMENTS: 'cached_appointments',

  // Settings
  THEME: 'theme',
  NOTIFICATIONS_ENABLED: 'notifications_enabled',
  BIOMETRICS_ENABLED: 'biometrics_enabled',

  // Onboarding
  ONBOARDING_COMPLETE: 'onboarding_complete',
  LAST_VERSION_SEEN: 'last_version_seen',
} as const;

/**
 * Auth token storage helpers
 */
export const authStorage = {
  async setTokens(accessToken: string, refreshToken?: string): Promise<void> {
    await setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken);
    if (refreshToken) {
      await setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    }
  },

  async getAccessToken(): Promise<string | null> {
    return getItem(STORAGE_KEYS.AUTH_TOKEN);
  },

  async getRefreshToken(): Promise<string | null> {
    return getItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  async clearTokens(): Promise<void> {
    await removeItem(STORAGE_KEYS.AUTH_TOKEN);
    await removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    await removeItem(STORAGE_KEYS.USER_ID);
    await removeItem(STORAGE_KEYS.USER_DATA);
  },

  async setUserData(userId: string, userData?: any): Promise<void> {
    await setItem(STORAGE_KEYS.USER_ID, userId);
    if (userData) {
      await setJSON(STORAGE_KEYS.USER_DATA, userData);
    }
  },

  async getUserId(): Promise<string | null> {
    return getItem(STORAGE_KEYS.USER_ID);
  },

  async getUserData<T>(): Promise<T | null> {
    return getJSON<T>(STORAGE_KEYS.USER_DATA);
  },
};

/**
 * Device registration storage helpers
 */
export const deviceStorage = {
  async setPushToken(token: string): Promise<void> {
    await setItem(STORAGE_KEYS.PUSH_TOKEN, token);
  },

  async getPushToken(): Promise<string | null> {
    return getItem(STORAGE_KEYS.PUSH_TOKEN);
  },

  async setVoipToken(token: string): Promise<void> {
    await setItem(STORAGE_KEYS.VOIP_TOKEN, token);
  },

  async getVoipToken(): Promise<string | null> {
    return getItem(STORAGE_KEYS.VOIP_TOKEN);
  },

  async setPushEnabled(enabled: boolean): Promise<void> {
    await setItem(STORAGE_KEYS.PUSH_ENABLED, enabled.toString());
  },

  async isPushEnabled(): Promise<boolean> {
    const value = await getItem(STORAGE_KEYS.PUSH_ENABLED);
    return value === 'true';
  },
};
